// @flow

import * as React from 'react';
import classNames from '@ta/common.classnames';

/**
 * export Button props so that other components that pass through props to
 * Button can easily use validation
 */
export type ButtonProps = {|
  /** The descendant React nodes of the Button */
  children?: React.Node,

  /** The callback */
  onClick?: (event: SyntheticUIEvent<*>) => mixed,

  /** button size. see styleguide for more information */
  /** fullwidth has been extracted into a separate property: TTD-14890. Refrain from using fullwidth here. Clean up JIRA TTD-15089 */
  size?: 'small' | 'default' | 'large' | 'fullwidth',
  /** whether button should contain a chevron */
  chevron?: boolean,
  /** whether button should contain loading state */
  loading?: boolean,
  /** whether button is disabled */
  disabled?: boolean,
  /** button type. see styleguide for more information */
  type?: 'none' | 'original' | 'primary' | 'standard' | 'secondary',
  /** whether button should have full width */
  fullwidth?: boolean,
  /** data attributes */
  dataAttributes?: { [string]: string },
|};

/**
 * Render a TripAdvisor ui_button.
 *
 * There are a number of different button types which can specified by the 'className' property:
 * - original
 * - primary
 * - standard
 * - secondary
 *
 * In addition there are a number of modifiers which can also specified by the 'className' property:
 * - disabled
 * - large
 * - small
 * - fullwidth
 * - chevron
 * - loading
 */
const Button = ({
  children,
  size,
  type,
  fullwidth,
  chevron,
  loading,
  disabled,
  onClick,
  dataAttributes,
}: ButtonProps) => (
  /**
   * If a left or right icon is desired, children will be wrapped in a span
   * so that we can vertically align it with the icons
   * @type {boolean}
   */
  <button
    className={classNames(
      'ui_button',
      type !== 'none' ? type : '',
      size !== 'default' ? size : '',
      {
        chevron,
        disabled,
        loading,
        fullwidth,
      },
    )}
    disabled={disabled}
    onClick={onClick}
    {...dataAttributes}
  >
    {children}
  </button>
);

// TODO: Filed as WP-868
/* istanbul ignore next */
Button.defaultProps = {
  children: null,
  onClick: () => {},
  type: 'original',
  size: 'default',
  chevron: false,
  loading: false,
  disabled: false,
  fullwidth: false,
};

export default Button;
